/**
 * Immersive mobile menu inside the header drawer: tabs per top-level item,
 * and sliding third-level screens with a back button.
 */
class MobileImmersiveMenu extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#resizeObserver = new ResizeObserver(() => this.#updateIndicator());
    const tabs = this.querySelector('.m-menu__tabs');
    if (tabs) this.#resizeObserver.observe(tabs);
    requestAnimationFrame(() => this.#updateIndicator());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#resizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-m-menu-tab]')]);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const tab = /** @type {HTMLElement | null} */ (target.closest('[data-m-menu-tab]'));
    if (tab) {
      this.#selectTab(tab);
      return;
    }

    const drill = /** @type {HTMLElement | null} */ (target.closest('[data-m-menu-drill]'));
    if (drill?.dataset.mMenuDrill) {
      const panel = drill.closest('[data-m-menu-panel]');
      if (panel instanceof HTMLElement) {
        this.#setLevel(panel, drill.dataset.mMenuDrill);
        if (event.detail === 0) {
          const back = panel.querySelector('[data-state="active"] [data-m-menu-back]');
          if (back instanceof HTMLElement) requestAnimationFrame(() => back.focus());
        }
      }
      return;
    }

    const back = target.closest('[data-m-menu-back]');
    if (back) {
      const panel = back.closest('[data-m-menu-panel]');
      const levelId = back.closest('[data-m-menu-level]')?.getAttribute('data-m-menu-level');
      if (panel instanceof HTMLElement) {
        this.#setLevel(panel, 'root');
        const previous = panel.querySelector(`[data-m-menu-drill="${levelId}"]`);
        if (event.detail === 0 && previous instanceof HTMLElement) requestAnimationFrame(() => previous.focus());
      }
    }
  };

  /**
   * Arrow key navigation between tabs.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const tab = /** @type {HTMLElement | null} */ (target?.closest('[data-m-menu-tab]') ?? null);
    if (!tab) return;

    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (event.key === 'Home') next = tabs[0];
    if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    event.preventDefault();
    this.#selectTab(next);
    next.focus();
  };

  /**
   * @param {HTMLElement} tab
   */
  #selectTab(tab) {
    const index = tab.dataset.mMenuTab;
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-m-menu-panel]')) {
      const el = /** @type {HTMLElement} */ (panel);
      const isActive = el.dataset.mMenuPanel === index;
      el.hidden = !isActive;
      if (isActive) this.#setLevel(el, 'root');
    }
    this.#updateIndicator();
    this.#scrollDrawerTop();
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} levelId
   */
  #setLevel(panel, levelId) {
    for (const level of panel.querySelectorAll('[data-m-menu-level]')) {
      const el = /** @type {HTMLElement} */ (level);
      const id = el.dataset.mMenuLevel;
      let state;
      if (levelId === 'root') {
        state = id === 'root' ? 'active' : 'next';
      } else {
        state = id === 'root' ? 'prev' : id === levelId ? 'active' : 'next';
      }
      el.dataset.state = state;
      el.inert = state !== 'active';
    }
    for (const drill of panel.querySelectorAll('[data-m-menu-drill]')) {
      drill.setAttribute('aria-expanded', String(/** @type {HTMLElement} */ (drill).dataset.mMenuDrill === levelId));
    }
    this.#scrollDrawerTop();
  }

  #scrollDrawerTop() {
    let scroller = this.parentElement;
    while (scroller && scroller !== document.body) {
      if (scroller.scrollHeight > scroller.clientHeight && getComputedStyle(scroller).overflowY !== 'visible') {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      scroller = scroller.parentElement;
    }
  }

  #updateIndicator() {
    const indicator = /** @type {HTMLElement | null} */ (this.querySelector('.m-menu__tab-indicator'));
    const selected = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!indicator || !selected) return;
    indicator.style.setProperty('--indicator-left', `${selected.offsetLeft}px`);
    indicator.style.setProperty('--indicator-width', `${selected.offsetWidth}px`);
  }
}

if (!customElements.get('mobile-immersive-menu')) {
  customElements.define('mobile-immersive-menu', MobileImmersiveMenu);
}
